import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { getTestProps } from '../../util/testProps';
import { MemberExtractionDocumentDetail } from './MemberExtractionDocumentDetail';
import { MemberExtractionDocumentList } from './MemberExtractionDocumentList';
import {
  MemberExtractionDocumentDetailSkeleton,
  MemberExtractionDocumentListSkeleton,
} from './MemberExtractionSkeletons';
import { clearMemberExtractionValueHistoryCache } from './memberExtractionHistory';
import { heroSpace } from './memberExtractionTheme';
import {
  MemberExtractionResourceRow,
  MemberExtractionReviewDecision,
  useMemberExtractionDocuments,
} from './useMemberExtractionDocuments';
import { useMemberExtractionReextractions } from './useMemberExtractionReextractions';
import { useRefreshMemberExtractionReviewCounts } from './useMemberExtractionReviewCounts';

export { useMemberExtractionReviewCounts } from './useMemberExtractionReviewCounts';
export type {
  MemberExtractionReviewCounts,
  MemberExtractionReviewCountsResult,
} from './useMemberExtractionReviewCounts';
export { useMemberExtractionDocuments } from './useMemberExtractionDocuments';
export type {
  MemberExtractionDocumentGroup,
  MemberExtractionDocumentsResult,
} from './useMemberExtractionDocuments';

const messages = defineMessages({
  empty: {
    id: 'phc-web-toolkit.memberExtractions.empty',
    defaultMessage: 'No extractions',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.error',
    defaultMessage: 'Unable to load extractions',
  },
  notFound: {
    id: 'phc-web-toolkit.memberExtractions.notFound',
    defaultMessage: 'This document has no extractions.',
  },
  backToList: {
    id: 'phc-web-toolkit.memberExtractions.backToList',
    defaultMessage: 'Back to all documents',
  },
});

const pageClassName = 'member-extraction-page';
const shineClassName = 'member-extraction-shine';

const pagePadding = heroSpace.regular;
const pageRadius = 8;

const frameStyle = {
  position: 'relative' as const,
  flex: 1,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
  borderRadius: pageRadius,
  backgroundColor: '#fff',
};

const pageStyle = {
  height: '100%',
  backgroundColor: '#fff',
};

const contentStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
  boxSizing: 'border-box' as const,
  padding: pagePadding,
};

// Search results come from an index that is updated a few seconds after each
// write, so reads that follow a write are repeated once it has caught up.
const INDEX_CATCH_UP_DELAYS_MS = [0, 5000, 15000];

// Coming back to the tab after this long reloads what others may have changed.
const REFOCUS_RELOAD_AFTER_MS = 60_000;

// Pending reads are dropped when `owner` changes, so they never land on
// another member's page.
const useAfterIndexCatchUp = (owner: string) => {
  const timers = React.useRef(new Set<number>());
  React.useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(timer => window.clearTimeout(timer));
      pending.clear();
    };
  }, [owner]);
  return React.useCallback((read: () => void) => {
    INDEX_CATCH_UP_DELAYS_MS.forEach(delay => {
      const timer = window.setTimeout(() => {
        timers.current.delete(timer);
        read();
      }, delay);
      timers.current.add(timer);
    });
  }, []);
};

const resourceKey = (resource: MemberExtractionResourceRow) =>
  `${resource.resourceType}/${resource.id}`;

type MemberExtractionsPageProps = {
  patientId: string;
  // Path of the document list; each document opens at `${basePath}/:id`.
  basePath: string;
  extractionId?: string;
};

export const MemberExtractionsPage: React.FC<MemberExtractionsPageProps> = ({
  patientId,
  basePath,
  extractionId,
}) => {
  const { formatMessage } = useIntl();
  const {
    groups: loadedGroups,
    isLoading,
    error,
    reload,
  } = useMemberExtractionDocuments(patientId);
  const refreshReviewCounts = useRefreshMemberExtractionReviewCounts(patientId);
  const afterIndexCatchUp = useAfterIndexCatchUp(patientId);
  // Documents aren't refetched after a review, so decisions made here are
  // layered on top to survive moving between the list and a document.
  const [decisions, setDecisions] = React.useState<
    Record<string, MemberExtractionReviewDecision>
  >({});
  const [resultsRevision, setResultsRevision] = React.useState(0);
  const loadedGroupsRef = React.useRef(loadedGroups);
  loadedGroupsRef.current = loadedGroups;

  const onReextracted = React.useCallback(
    (sourceReference: string) => {
      // The run may have rewritten this document's results and cleared their
      // review, so decisions made on them here no longer hold.
      const rewritten = new Set(
        loadedGroupsRef.current
          .filter(group => group.sourceReference === sourceReference)
          .flatMap(group => group.resources.map(resourceKey)),
      );
      setDecisions(current =>
        Object.fromEntries(
          Object.entries(current).filter(([key]) => !rewritten.has(key)),
        ),
      );
      afterIndexCatchUp(() => {
        clearMemberExtractionValueHistoryCache();
        refreshReviewCounts();
        reload().then(() => setResultsRevision(revision => revision + 1));
      });
    },
    [afterIndexCatchUp, refreshReviewCounts, reload],
  );
  const reextractions = useMemberExtractionReextractions(
    patientId,
    onReextracted,
  );

  const onReviewed = React.useCallback(
    (
      resource: MemberExtractionResourceRow,
      decision: MemberExtractionReviewDecision,
    ) => {
      setDecisions(current => ({
        ...current,
        [resourceKey(resource)]: decision,
      }));
      afterIndexCatchUp(() => {
        clearMemberExtractionValueHistoryCache();
        refreshReviewCounts();
      });
    },
    [afterIndexCatchUp, refreshReviewCounts],
  );

  React.useEffect(() => {
    let hiddenAt: number | undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (
        hiddenAt !== undefined &&
        Date.now() - hiddenAt >= REFOCUS_RELOAD_AFTER_MS
      ) {
        clearMemberExtractionValueHistoryCache();
        refreshReviewCounts();
        reload();
      }
      hiddenAt = undefined;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refreshReviewCounts, reload]);

  const groups = React.useMemo(
    () =>
      loadedGroups.map(group => ({
        ...group,
        resources: group.resources.map(resource => {
          const decision = decisions[resourceKey(resource)];
          return decision
            ? { ...resource, reviewDecision: decision }
            : resource;
        }),
      })),
    [decisions, loadedGroups],
  );
  const selectedGroup = extractionId
    ? groups.find(group => group.id === extractionId)
    : undefined;

  const scrolling = (content: React.ReactNode) => (
    <SimpleBar
      className={pageClassName}
      style={pageStyle}
      {...getTestProps('member-extractions-page')}
    >
      <div style={contentStyle}>{content}</div>
    </SimpleBar>
  );

  let page: React.ReactNode;
  if (isLoading) {
    page = (
      <div style={pageStyle} {...getTestProps('member-extractions-page')}>
        {extractionId ? (
          <MemberExtractionDocumentDetailSkeleton />
        ) : (
          <MemberExtractionDocumentListSkeleton />
        )}
      </div>
    );
  } else if (error) {
    page = scrolling(
      <Text size="subbody">{formatMessage(messages.error)}</Text>,
    );
  } else if (selectedGroup) {
    page = (
      <div style={pageStyle} {...getTestProps('member-extractions-page')}>
        <MemberExtractionDocumentDetail
          patientId={patientId}
          group={selectedGroup}
          basePath={basePath}
          onReviewed={onReviewed}
          reextracting={reextractions.isRunning(selectedGroup.sourceReference)}
          onReextractStarted={taskId =>
            reextractions.track(selectedGroup.sourceReference, taskId)
          }
          resultsRevision={resultsRevision}
        />
      </div>
    );
  } else if (extractionId) {
    page = scrolling(
      <>
        <Text size="subbody">{formatMessage(messages.notFound)}</Text>
        <Link to={basePath}>{formatMessage(messages.backToList)}</Link>
      </>,
    );
  } else if (groups.length === 0) {
    page = scrolling(
      <Text size="subbody">{formatMessage(messages.empty)}</Text>,
    );
  } else {
    page = scrolling(
      <MemberExtractionDocumentList groups={groups} basePath={basePath} />,
    );
  }

  return (
    <div style={frameStyle}>
      <style>
        {`
          @keyframes member-extraction-shine {
            0% { background-position: 0% 0%; }
            50% { background-position: 100% 100%; }
            100% { background-position: 0% 0%; }
          }
          .${shineClassName} {
            position: absolute;
            inset: 0;
            z-index: 3;
            pointer-events: none;
            border-radius: ${pageRadius}px;
            padding: 1px;
            opacity: 0.55;
            background-image: radial-gradient(transparent, transparent, #9fd4f2, #c4b5fd, #0083ce, #f5b6c8, #f3d2a6, transparent, transparent);
            background-size: 300% 300%;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            animation: member-extraction-shine 18s infinite linear;
            will-change: background-position;
          }
          @media (prefers-reduced-motion: reduce) {
            .${shineClassName} { animation: none; }
          }
        `}
      </style>
      <div aria-hidden className={shineClassName} />
      {page}
    </div>
  );
};
